import { motion } from 'framer-motion';
import { useEffect, useRef, type ReactNode } from 'react';

const EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];
const HIDDEN = { opacity: 0, y: 20, scale: 0.95 };
const VISIBLE = { opacity: 1, y: 0, scale: 1 };

interface RevealOnScrollProps {
  id?: string;
  className?: string;
  /** Retardo del stagger de la animación de entrada. */
  delay?: number;
  /** Sin animación de entrada: aparece visible al instante. Para reduced-motion
   *  o al volver de cerrar (restore) — así no re-anima al restaurar/subir. */
  disabled?: boolean;
  /** Solo anima cuando la página está lista (transición de entrada terminada).
   *  Si la animación corre durante la transición, se ejecuta fuera de vista y no
   *  se ve. Por defecto true. */
  ready?: boolean;
  /** Se llama una vez cuando la card queda visible (tras animar o al instante).
   *  Lo usa el grid para mostrar "Cargar más" / la siguiente sección. */
  onShown?: () => void;
  children: ReactNode;
}

/**
 * Envuelve una card con animación de entrada al aparecer en el viewport. Si
 * `disabled` (reduced-motion o al volver de cerrar) aparece sin animación, de
 * modo que no re-anima al restaurar el scroll ni al subir.
 */
export function RevealOnScroll({
  id,
  className,
  delay = 0,
  disabled = false,
  ready = true,
  onShown,
  children,
}: RevealOnScrollProps) {
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  const firedRef = useRef(false);
  const fireOnce = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onShownRef.current?.();
  };

  // Deshabilitado → no hay animación que esperar; avisamos en el montaje.
  useEffect(() => {
    if (disabled) fireOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  return (
    <motion.div
      id={id}
      className={className}
      initial={disabled ? false : HIDDEN}
      whileInView={disabled || !ready ? undefined : VISIBLE}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.42, delay, ease: EASE }}
      onAnimationComplete={() => {
        if (!disabled) fireOnce();
      }}
    >
      {children}
    </motion.div>
  );
}

export default RevealOnScroll;
